import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from ".././Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";

const TITLE = "Calculatrice d'aire des formes géométriques";
const DESCRIPTION =
  "Calculez l'aire de diverses formes géométriques courantes : rectangle, triangle, cercle, trapèze, ellipse et secteur. Outil gratuit avec formules détaillées.";
const KEYWORDS =
  "calculatrice aire, aire rectangle, aire triangle, aire cercle, aire trapèze, aire ellipse, formes géométriques";
const OG_URL = "https://do-calculate.com/calculator/fr/areas/";

const shapes = [
  { key: "rectangle", label: "Rectangle" },
  { key: "triangle", label: "Triangle (Héron)" },
  { key: "circle", label: "Cercle" },
  { key: "trapezoid", label: "Trapèze" },
  { key: "ellipse", label: "Ellipse" },
  { key: "sector", label: "Secteur circulaire" },
  { key: "parallelogram", label: "Parallélogramme" },
];

function calcArea(shape, vals) {
  const n = (k) => parseFloat(vals[k]);
  switch (shape) {
    case "rectangle":    return n("length") * n("width");
    case "triangle": {
      const [a, b, c] = [n("a"), n("b"), n("c")];
      const s = (a + b + c) / 2;
      const v = s * (s - a) * (s - b) * (s - c);
      return v > 0 ? Math.sqrt(v) : NaN;
    }
    case "circle":       return Math.PI * n("radius") ** 2;
    case "trapezoid":    return 0.5 * (n("base1") + n("base2")) * n("height");
    case "ellipse":      return Math.PI * n("a") * n("b");
    case "sector":       return 0.5 * n("radius") ** 2 * (n("angle") * Math.PI / 180);
    case "parallelogram":return n("base") * n("height");
    default:             return NaN;
  }
}

const fields = {
  rectangle:     [{ key: "length", label: "Longueur (m)" }, { key: "width", label: "Largeur (m)" }],
  triangle:      [{ key: "a", label: "Côté a (m)" }, { key: "b", label: "Côté b (m)" }, { key: "c", label: "Côté c (m)" }],
  circle:        [{ key: "radius", label: "Rayon (m)" }],
  trapezoid:     [{ key: "base1", label: "Base 1 (m)" }, { key: "base2", label: "Base 2 (m)" }, { key: "height", label: "Hauteur (m)" }],
  ellipse:       [{ key: "a", label: "Demi-grand axe a (m)" }, { key: "b", label: "Demi-petit axe b (m)" }],
  sector:        [{ key: "radius", label: "Rayon (m)" }, { key: "angle", label: "Angle (degrés)" }],
  parallelogram: [{ key: "base", label: "Base (m)" }, { key: "height", label: "Hauteur (m)" }],
};

const formulas = {
  rectangle:     "A = longueur × largeur",
  triangle:      "A = √(s(s−a)(s−b)(s−c))  où s = (a+b+c)/2",
  circle:        "A = π × r²",
  trapezoid:     "A = ½ × (b₁ + b₂) × h",
  ellipse:       "A = π × a × b",
  sector:        "A = ½ × r² × θ  (θ en radians)",
  parallelogram: "A = base × hauteur",
};

function AreaCalculators() {
  const [shape, setShape] = useState("rectangle");
  const [vals, setVals] = useState({});
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleChange = (k, v) => setVals((p) => ({ ...p, [k]: v }));

  const handleCalculate = () => {
    const required = fields[shape].map((f) => f.key);
    for (const k of required) {
      if (!vals[k] || isNaN(parseFloat(vals[k])) || parseFloat(vals[k]) < 0) {
        setError(`Veuillez entrer une valeur valide pour : ${fields[shape].find(f => f.key === k)?.label}`);
        setResult(null);
        return;
      }
    }
    const area = calcArea(shape, vals);
    if (isNaN(area) || area < 0) {
      setError("Les valeurs saisies ne permettent pas de former une forme valide.");
      setResult(null);
    } else {
      setError("");
      setResult(area);
    }
  };

  const handleShapeChange = (s) => { setShape(s); setVals({}); setResult(null); setError(""); };

  return (
    <div>
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} />

      <div className="row"><div className="col-12 col-md-8"><FirstAds /></div></div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ height: "50px" }}>
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>

          <div className="border border-light">
            <div className="row">
              <div>
                <p>Cette calculatrice calcule l'aire de plusieurs formes géométriques courantes incluant le rectangle, le triangle, le cercle, le trapèze, l'ellipse, le secteur circulaire et le parallélogramme.</p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />

            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />

                {/* Shape selector */}
                <div className="form-group">
                  <label><b>Sélectionner la forme :</b></label>
                  <select className="form-control" value={shape} onChange={(e) => handleShapeChange(e.target.value)}>
                    {shapes.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>

                <div className="alert alert-info" style={{ fontSize: "13px" }}>
                  <b>Formule :</b> {formulas[shape]}
                </div>

                {fields[shape].map((f) => (
                  <div className="form-group" key={f.key}>
                    <label>{f.label} :</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      placeholder={`Entrez ${f.label}`}
                      value={vals[f.key] || ""}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                    />
                  </div>
                ))}

                <button className="btn btn-success" onClick={handleCalculate}>Calculer</button>

                {error && <p className="p-3 mb-2 bg-warning text-dark mt-2">{error}</p>}
                {result !== null && (
                  <div className="p-3 mb-2 bg-success text-white mt-2">
                    <b>Aire = {result.toLocaleString("fr-FR", { maximumFractionDigits: 4 })} m²</b>
                  </div>
                )}
              </div>

              <div className="col">
                <SecondAds />
                <MainCalculatorsList />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 col-md-8">
        <div className="info-sections-container">
          <div className="info-section">
            <div className="info-section-header"><u><h3 style={{ fontSize: "16px" }}>Aire</h3></u></div>
            <div className="info-section-body">
              <p>L'aire est une grandeur qui décrit la taille ou l'étendue d'une forme à deux dimensions dans un plan. On peut la visualiser comme la quantité de peinture nécessaire pour couvrir une surface. L'unité de mesure standard de l'aire dans le Système international d'unités (SI) est le mètre carré (m²). Voici les formules des formes les plus courantes avec des exemples de calcul pour chacune.</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><u><h3 style={{ fontSize: "16px" }}>Calcul de l'aire du rectangle</h3></u></div>
            <div className="info-section-body">
              <p>Le rectangle est un quadrilatère à quatre angles droits. Son aire se calcule en multipliant la longueur par la largeur.</p>
              <p><strong>Aire = longueur × largeur</strong></p>
              <p>Par exemple, si la longueur est 10 m et la largeur 5 m :</p>
              <code>Aire = 10 m × 5 m = 50 m²</code>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><u><h3 style={{ fontSize: "16px" }}>Calcul de l'aire du triangle</h3></u></div>
            <div className="info-section-body">
              <p>Avec la formule de Héron, connaissant les trois côtés a, b, c : s = (a+b+c)/2 et A = √(s(s−a)(s−b)(s−c)).</p>
              <p><strong>Aire = √(s(s−a)(s−b)(s−c)) où s = (a+b+c)/2</strong></p>
              <p>Par exemple, un triangle de côtés 3, 4 et 5 m : s = 6, Aire = √(6×3×2×1) = 6 m².</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><u><h3 style={{ fontSize: "16px" }}>Calcul de l'aire du cercle</h3></u></div>
            <div className="info-section-body">
              <p>L'aire d'un cercle se calcule à partir de son rayon.</p>
              <p><strong>Aire = π × r²</strong></p>
              <p>Par exemple, un cercle de rayon 5 m : Aire = π × 25 ≈ 78,54 m².</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><u><h3 style={{ fontSize: "16px" }}>Calcul de l'aire du secteur circulaire</h3></u></div>
            <div className="info-section-body">
              <p>Un secteur circulaire est une portion de disque délimitée par deux rayons et un arc. Son aire dépend du rayon et de l'angle au centre.</p>
              <p><strong>Aire = ½ × r² × θ (θ en radians)</strong></p>
              <p>Par exemple, un secteur de rayon 6 m et d'angle 60° (π/3 rad) : Aire = ½ × 36 × π/3 ≈ 18,85 m².</p>
            </div>
          </div>
          <div className="info-section">
            <div className="info-section-header"><u><h3 style={{ fontSize: "16px" }}>Calcul de l'aire du parallélogramme</h3></u></div>
            <div className="info-section-body">
              <p>Un parallélogramme est un quadrilatère dont les côtés opposés sont parallèles deux à deux. Son aire se calcule par la base multipliée par la hauteur.</p>
              <p><strong>Aire = base × hauteur</strong></p>
              <p>Par exemple, un parallélogramme de base 8 m et de hauteur 5 m : Aire = 8 × 5 = 40 m².</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row"><div className="col-12 col-md-8"><RelatedLinks /></div></div>
    </div>
  );
}

export default AreaCalculators;
